import { IconX } from "@tabler/icons-react";
import NoDataFound from "../../../components/common/NoDataFound";
import NormalSearch from "../../../components/common/NormalSearch";
import { useMemo, useState } from "react";

type CityBreakdownRow = {
  city: string;
  total: string | number;
  ofd: string | number;
  delivered: string | number;
  notDelivered: string | number;
  successRate: number;
};

type ViewCitiesModalProps = {
  isOpen: boolean;
  region: string;
  cities: CityBreakdownRow[];
  onClose: () => void;
};

export default function ViewCitiesPriceMatrixModal({
  isOpen,
  region,
  cities,
  onClose,
}: ViewCitiesModalProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredCities = useMemo(() => {
    return cities.filter((row) =>
      row.city?.toLowerCase().includes(searchTerm?.toLowerCase()),
    );
  }, [cities, searchTerm]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-[#F7F6F2] rounded-3xl w-[500px] max-h-[76vh] flex flex-col  overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-medium text-[#212121]">
            {region || "Region"} Cities ({cities.length})
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full border border-[#D2D2D2]"
          >
            <IconX size={20} />
          </button>
        </div>

        {/* Search + Filter */}
        <div className="px-4 py-3">
          <NormalSearch
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex-1 rounded-3xl bg-white/80 border overflow-hidden">
          <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1.2fr_1fr] gap-4 px-5 py-4 bg-[#f7f6f2] text-sm font-semibold">
            <p className="text-center">City</p>
            <p className="text-center">Total</p>
            <p className="text-center">OFD</p>
            <p className="text-center">Del</p>
            <p className="text-center">N-Del</p>
            <p className="text-center">Per%</p>
          </div>

          <div className="max-h-80 overflow-y-auto divide-y">
            {filteredCities.length === 0 ? (
              <NoDataFound />
            ) : (
              filteredCities.map((row) => (
                <div
                  key={row.city}
                  className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1.2fr_1fr] gap-4 px-5 py-3 items-center"
                >
                  <p className="truncate text-center text-xs">{row.city}</p>
                  <p className="text-center text-xs">{row.total}</p>
                  <p className="text-center text-xs">{row.ofd}</p>
                  <p className="text-center bg-green-100 rounded-full text-xs">
                    {row.delivered}
                  </p>
                  <p className="text-center bg-red-100 rounded-full text-xs">
                    {row.notDelivered}
                  </p>
                  <div>
                    <div className="h-2 w-14 overflow-hidden rounded-full bg-[#D2D2D2]">
                      <div
                        className="h-full rounded-full bg-[#22C55E]"
                        style={{ width: `${Math.min(row.successRate, 100)}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-center">
                      {row.successRate}%
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
